import { useState, useEffect, useRef, useMemo } from 'react';
import { productService, type ProductListItem } from 'services/product.service';
import { SortOption } from 'components/sections/shop/utils/constants';
import { usePricingSession } from 'hooks/usePricingAuthKey';

interface ProductListParams {
  categoryName?: string;
  categoryId?: number;
  search?: string;
  page: number;
  size: number;
  sort: SortOption;
  currencyId?: number;
}

interface Pagination {
  page: number;
  size: number;
  totalCount: number;
  totalPages: number;
}

interface UseProductsReturn {
  products: ProductListItem[];
  pagination: Pagination;
  loading: boolean; // Initial loading (when no products exist)
  loadingMore: boolean; // Loading more products (pagination)
  error: Error | null;
}

// Module-level request cache to prevent duplicate requests across component instances
const LIST_CACHE_VERSION = 'v2-qb';
const requestCache = new Map<string, Promise<{ items: ProductListItem[]; pagination: Pagination }>>();

/** Clear cached list responses so login/logout refetches customer prices. */
export function clearProductListRequestCache(): void {
  requestCache.clear();
}

/**
 * Custom hook to fetch products with filtering, sorting, and pagination
 * Includes request deduplication and abort handling
 */
export const useProducts = (
  categoryName: string,
  sortBy: SortOption,
  currentPage: number,
  currencyId?: number,
  searchQuery?: string,
  categoryId?: number,
): UseProductsReturn => {
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    size: 20,
    totalCount: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(false); // Initial loading
  const [loadingMore, setLoadingMore] = useState(false); // Loading more (pagination)
  const [error, setError] = useState<Error | null>(null);

  const { pricingAuthKey, isAuthReady } = usePricingSession();
  const lastRequestRef = useRef<string>('');
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastFilterKeyRef = useRef<string>(''); // Track filter state (without page) to detect if only page changed
  const isInitialMountRef = useRef<boolean>(true); // Track if this is the initial mount

  // Create filter key without page to detect if only page changed
  const filterKey = useMemo(
    () => `${LIST_CACHE_VERSION}-${categoryName}-${categoryId || ''}-${searchQuery || ''}-${sortBy}-${currencyId || ''}-${pricingAuthKey}`,
    [categoryName, categoryId, searchQuery, sortBy, currencyId, pricingAuthKey]
  );

  useEffect(() => {
    clearProductListRequestCache();
    lastRequestRef.current = '';
    lastFilterKeyRef.current = '';
  }, [pricingAuthKey]);

  useEffect(() => {
    if (!isAuthReady) {
      return;
    }

    if (!categoryName && !categoryId && !searchQuery) {
      return;
    }

    // Create a unique request key
    const requestKey = `${filterKey}-${currentPage}`;

    // Determine if only the page changed (filters stayed the same)
    const isLoadMore = lastFilterKeyRef.current === filterKey && lastFilterKeyRef.current !== '';
    
    // Detect if this is initial mount with page > 1 (user refreshed on a later page)
    const isInitialMountWithPage = isInitialMountRef.current && currentPage > 1;
    
    // If filters changed (not a load more), clear products immediately and set loading state
    // This ensures loading indicator shows immediately when search/filters change
    if (!isLoadMore) {
      if (lastFilterKeyRef.current !== '') {
        // Filters changed - clear existing products
        setProducts([]);
      }
      // Set loading immediately (for both initial load and filter changes) so UI shows loading state right away
      setLoading(true);
      setError(null);
    }
    
    // Skip if this exact request was already made
    if (lastRequestRef.current === requestKey && !isInitialMountWithPage) {
      return;
    }
    
    // Update filter key ref
    lastFilterKeyRef.current = filterKey;
    
    // Mark that initial mount is complete after first run
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
    }

    // Skip if there's already an active request with the same key
    const cachedRequest = requestCache.get(requestKey);
    if (cachedRequest) {
      // Set appropriate loading state for cached request
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      cachedRequest
        .then((result) => {
          if (lastRequestRef.current === requestKey) {
            // Append products if loading more, replace if filters changed
            setProducts((prevProducts) => {
              if (isLoadMore) {
                // Deduplicate by variantId when appending
                const existingIds = new Set(prevProducts.map(p => p.variantId));
                const newItems = result.items.filter(item => !existingIds.has(item.variantId));
                return [...prevProducts, ...newItems];
              }
              return result.items;
            });
            setPagination(result.pagination);
            setLoading(false);
            setLoadingMore(false);
            setError(null);
          }
        })
        .catch((err) => {
          if (lastRequestRef.current === requestKey) {
            setError(err instanceof Error ? err : new Error('Failed to fetch products'));
            setProducts([]);
            setPagination({
              page: 1,
              size: 20,
              totalCount: 0,
              totalPages: 0,
            });
            setLoading(false);
            setLoadingMore(false);
          }
        });
      return;
    }

    const fetchProducts = async () => {
      // Cancel any previous request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Mark this request as in progress
      lastRequestRef.current = requestKey;

      try {
        setError(null);
        // Set appropriate loading state
        if (isLoadMore) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }

        // If this is initial mount with page > 1, fetch all pages from 1 to currentPage
        // This restores the user's view state on page refresh
        if (isInitialMountWithPage) {
          const allProducts: ProductListItem[] = [];
          let finalPagination: Pagination | null = null;

          // Fetch all pages sequentially from 1 to currentPage
          for (let page = 1; page <= currentPage; page++) {
            // Check if aborted before each request
            if (abortController.signal.aborted) {
              return;
            }

            const pageParams: ProductListParams = {
              page,
              size: 20,
              sort: sortBy,
              currencyId,
            };

            // Add category or search (search takes precedence if both exist)
            if (searchQuery) {
              pageParams.search = searchQuery;
            } else if (categoryId) {
              pageParams.categoryId = categoryId;
            } else if (categoryName) {
              pageParams.categoryName = categoryName;
            }

            // Create request key for this page
            const pageRequestKey = `${filterKey}-${page}`;
            
            // Check cache first
            let pageResult;
            const cachedPageRequest = requestCache.get(pageRequestKey);
            if (cachedPageRequest) {
              pageResult = await cachedPageRequest;
            } else {
              // Create the promise and cache it
              const requestPromise = productService.getProducts(pageParams);
              requestCache.set(pageRequestKey, requestPromise);
              pageResult = await requestPromise;
              // Keep in cache for potential reuse
            }

            // Deduplicate products by variantId
            const existingIds = new Set(allProducts.map(p => p.variantId));
            const newItems = pageResult.items.filter(item => !existingIds.has(item.variantId));
            allProducts.push(...newItems);

            // Keep the pagination from the last (current) page
            if (page === currentPage) {
              finalPagination = pageResult.pagination;
            }
          }

          // Only update if this is still the latest request and not aborted
          if (lastRequestRef.current === requestKey && !abortController.signal.aborted && finalPagination) {
            setProducts(allProducts);
            setPagination(finalPagination);
          }
        } else {
          // Normal fetch for single page
          const params: ProductListParams = {
            page: currentPage,
            size: 20,
            sort: sortBy,
            currencyId,
          };

          // Add category or search (search takes precedence if both exist)
          if (searchQuery) {
            params.search = searchQuery;
          } else if (categoryId) {
            params.categoryId = categoryId;
          } else if (categoryName) {
            params.categoryName = categoryName;
          }

          // Create the promise and cache it
          const requestPromise = productService.getProducts(params);
          requestCache.set(requestKey, requestPromise);

          const result = await requestPromise;

          // Remove from cache after completion (only for non-initial loads to allow reuse)
          if (!isInitialMountWithPage) {
            requestCache.delete(requestKey);
          }

          // Only update if this is still the latest request and not aborted
          if (lastRequestRef.current === requestKey && !abortController.signal.aborted) {
            // Append products if loading more, replace if filters changed
            setProducts((prevProducts) => {
              if (isLoadMore) {
                // Deduplicate by variantId when appending
                const existingIds = new Set(prevProducts.map(p => p.variantId));
                const newItems = result.items.filter(item => !existingIds.has(item.variantId));
                return [...prevProducts, ...newItems];
              }
              return result.items;
            });
            setPagination(result.pagination);
          }
        }
      } catch (err: any) {
        // Remove from cache on error
        requestCache.delete(requestKey);

        // Ignore abort errors
        if (err?.name === 'AbortError') {
          return;
        }

        // Only update if this is still the latest request and not aborted
        if (lastRequestRef.current === requestKey && !abortController.signal.aborted) {
          setError(err instanceof Error ? err : new Error('Failed to fetch products'));
          setProducts([]);
          setPagination({
            page: 1,
            size: 20,
            totalCount: 0,
            totalPages: 0,
          });
        }
      } finally {
        // Only update loading if this is still the latest request and not aborted
        if (lastRequestRef.current === requestKey && !abortController.signal.aborted) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    };

    // Execute immediately - no debounce needed since all changes are discrete user actions
    // (category clicks, filter selections, etc.) and we already have request deduplication
    // and abort controller protection
    fetchProducts();

    return () => {
      // Cancel any pending request on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [categoryName, categoryId, searchQuery, sortBy, currentPage, currencyId, filterKey, isAuthReady]);

  return { products, pagination, loading, loadingMore, error };
};

